<template>
  <ShopLte title="Quản lý Giao dịch">
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="m-0">Quản lý Giao dịch</h1>
        <div>
          <button
            @click="exportTransactions"
            class="btn btn-success me-2"
            :disabled="exporting"
          >
            <i class="fas fa-file-excel"></i>
            {{ exporting ? 'Đang xuất...' : 'Xuất Excel' }}
          </button>
          <Link
            :href="route('shop.financial.transactions.create')"
            class="btn btn-primary"
          >
            <i class="fas fa-plus"></i> Tạo giao dịch
          </Link>
        </div>
      </div>
    </template>

    <!-- Balance Summary -->
    <div class="row mb-4">
      <div class="col-lg-3 col-6">
        <div class="small-box bg-info">
          <div class="inner">
            <h3>{{ formatCurrency(balance.current_balance) }}</h3>
            <p>Số dư hiện tại</p>
          </div>
          <div class="icon">
            <i class="fas fa-wallet"></i>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-6">
        <div class="small-box bg-success">
          <div class="inner">
            <h3>{{ formatCurrency(summary.total_income) }}</h3>
            <p>Tổng thu</p>
          </div>
          <div class="icon">
            <i class="fas fa-arrow-up"></i>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-6">
        <div class="small-box bg-danger">
          <div class="inner">
            <h3>{{ formatCurrency(summary.total_expense) }}</h3>
            <p>Tổng chi</p>
          </div>
          <div class="icon">
            <i class="fas fa-arrow-down"></i>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-6">
        <div class="small-box bg-warning">
          <div class="inner">
            <h3>{{ formatCurrency(summary.net_flow) }}</h3>
            <p>Dòng tiền ròng</p>
          </div>
          <div class="icon">
            <i class="fas fa-balance-scale"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">Bộ lọc</h3>
        <div class="card-tools">
          <button
            type="button"
            class="btn btn-tool"
            data-card-widget="collapse"
          >
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters">
          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label>Loại giao dịch</label>
                <select class="form-control" v-model="filters.trans_type">
                  <option value="">Tất cả</option>
                  <option value="1">Thu</option>
                  <option value="2">Chi</option>
                </select>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Từ ngày</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.date_from"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Đến ngày</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.date_to"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Đối tác</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="filters.partner_search"
                  placeholder="Tìm theo tên đối tác..."
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Số tiền từ</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="filters.amount_from"
                  placeholder="0"
                  step="1000"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Số tiền đến</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="filters.amount_to"
                  placeholder="Không giới hạn"
                  step="1000"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <button type="submit" class="btn btn-primary">
                <i class="fas fa-search"></i> Áp dụng bộ lọc
              </button>
              <button
                type="button"
                @click="resetFilters"
                class="btn btn-secondary ml-2"
              >
                <i class="fas fa-undo"></i> Đặt lại
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="card mt-4">
      <div class="card-header">
        <h3 class="card-title">Danh sách giao dịch ({{ transactions.total }} bản ghi)</h3>
        <div class="card-tools">
          <div class="input-group input-group-sm" style="width: 250px;">
            <input
              type="text"
              class="form-control float-right"
              placeholder="Tìm kiếm..."
              v-model="searchQuery"
              @input="debounceSearch"
            />
            <div class="input-group-append">
              <button type="button" class="btn btn-default">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Ngày</th>
                <th>Loại</th>
                <th>Đối tác</th>
                <th>Số tiền</th>
                <th>Nguồn gốc</th>
                <th>Ghi chú</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="transactions.data.length === 0">
                <td colspan="7" class="text-center text-muted py-4">
                  Không có giao dịch nào
                </td>
              </tr>
              <tr
                v-for="transaction in transactions.data"
                :key="transaction.id"
              >
                <td>
                  <div>{{ formatDate(transaction.trans_date) }}</div>
                  <small class="text-muted">{{ formatTime(transaction.trans_date) }}</small>
                </td>
                <td>
                  <span
                    :class="[
                      'badge badge-sm',
                      transaction.trans_type === 1 ? 'badge-success' : 'badge-danger'
                    ]"
                  >
                    {{ transaction.trans_type === 1 ? 'Thu' : 'Chi' }}
                  </span>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm me-2">
                      <div class="avatar-title bg-light rounded-circle">
                        <i class="fas fa-user text-muted"></i>
                      </div>
                    </div>
                    <div>
                      <div class="font-weight-bold">{{ transaction.partner_name || 'N/A' }}</div>
                      <small class="text-muted">
                        {{ getPartnerTypeLabel(transaction.partner_type) }}
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <div
                    :class="[
                      'font-weight-bold',
                      transaction.trans_type === 1 ? 'text-success' : 'text-danger'
                    ]"
                  >
                    {{ formatCurrency(transaction.amount) }}
                  </div>
                  <small class="text-muted">{{ transaction.currency || 'VND' }}</small>
                </td>
                <td>
                  <div v-if="transaction.ref_type">
                    {{ getRefTypeLabel(transaction.ref_type) }}
                    <small class="text-muted">#{{ transaction.ref_id }}</small>
                  </div>
                  <div v-else class="text-muted">
                    Giao dịch thủ công
                  </div>
                </td>
                <td>
                  <div class="text-truncate" style="max-width: 200px;">
                    {{ transaction.note || 'Không có ghi chú' }}
                  </div>
                </td>
                <td>
                  <div class="btn-group">
                    <Link
                      :href="route('shop.financial.transactions.show', transaction.id)"
                      class="btn btn-sm btn-info"
                    >
                      <i class="fas fa-eye"></i>
                    </Link>
                    <Link
                      :href="route('shop.financial.transactions.edit', transaction.id)"
                      class="btn btn-sm btn-warning"
                    >
                      <i class="fas fa-edit"></i>
                    </Link>
                    <button
                      @click="deleteTransaction(transaction)"
                      class="btn btn-sm btn-danger"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer">
        <Pagination :links="transactions.links" />
      </div>
    </div>
  </ShopLte>
</template>

<script>
import { ref, reactive } from 'vue'
// import { Link } from '@inertiajs/vue3'
import ShopLte from '@/Layouts/ShopLte.vue'
import Pagination from '@/JetstreamBootstrap/Pagination.vue'

export default {
  name: 'ShopTransactionIndex',
  components: {
    ShopLte,
    Link,
    Pagination
  },
  props: {
    transactions: {
      type: Object,
      required: true
    },
    summary: {
      type: Object,
      default: () => ({
        total_income: 0,
        total_expense: 0,
        net_flow: 0,
        total_transactions: 0
      })
    },
    balance: {
      type: Object,
      default: () => ({
        current_balance: 0
      })
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    // Reactive data
    const searchQuery = ref('')
    const exporting = ref(false)

    const localFilters = reactive({
      trans_type: props.filters.trans_type || '',
      date_from: props.filters.date_from || '',
      date_to: props.filters.date_to || '',
      partner_search: props.filters.partner_search || '',
      amount_from: props.filters.amount_from || '',
      amount_to: props.filters.amount_to || ''
    })

    // Methods
    const applyFilters = () => {
      router.get(route('shop.financial.transactions.index'), localFilters, {
        preserveState: true,
        preserveScroll: true
      })
    }

    const resetFilters = () => {
      Object.assign(localFilters, {
        trans_type: '',
        date_from: '',
        date_to: '',
        partner_search: '',
        amount_from: '',
        amount_to: ''
      })
      applyFilters()
    }

    const debounceSearch = () => {
      clearTimeout(window.searchTimeout)
      window.searchTimeout = setTimeout(() => {
        localFilters.partner_search = searchQuery.value
        applyFilters()
      }, 500)
    }

    const deleteTransaction = (transaction) => {
      if (confirm('Bạn có chắc chắn muốn xóa giao dịch này?')) {
        router.delete(route('shop.financial.transactions.destroy', transaction.id))
      }
    }

    const exportTransactions = () => {
      exporting.value = true
      const params = new URLSearchParams(localFilters)
      window.open(route('shop.financial.transactions.export') + '?' + params.toString())
      setTimeout(() => {
        exporting.value = false
      }, 1000)
    }

    // Utility methods
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('vi-VN')
    }

    const formatTime = (date) => {
      return new Date(date).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getPartnerTypeLabel = (type) => {
      const types = {
        'App\\Models\\Customer': 'Khách hàng',
        'App\\Models\\Partner': 'Nhà cung cấp'
      }
      return types[type] || type
    }

    const getRefTypeLabel = (type) => {
      const types = {
        'deal': 'Đơn hàng',
        'flow': 'Nhập hàng',
        'debt': 'Công nợ'
      }
      return types[type] || type
    }

    return {
      // Reactive data
      searchQuery,
      exporting,
      localFilters,

      // Methods
      applyFilters,
      resetFilters,
      debounceSearch,
      deleteTransaction,
      exportTransactions,

      // Utility methods
      formatCurrency,
      formatDate,
      formatTime,
      getPartnerTypeLabel,
      getRefTypeLabel
    }
  }
}
</script>
